import React, { useContext, useEffect, useState } from "react";
import { IconBolt, IconHeart, IconLock, IconBuildingBank } from "@tabler/icons-react";
import { DataContext } from "../../../context/DataContext";
import InitialLoadingScreen, {
  getLoadingMessage,
} from "../../../components/initial-loading-screen";
import TrinsicRedirectFallback from "./TrinsicRedirectFallback";
import { sendProviderTabTo } from "./providerTab";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../../../utils/edge";

const FALLBACK_DELAY_MS = 4000;

const defaultFeatures = [
  { icon: IconBolt, text: "Your bank instantly verifies you" },
  { icon: IconLock, text: "Keep your data safe and secure" },
  { icon: IconHeart, text: "No need to provide documents" },
];

const TrinsicStart = ({
  providerName,
  providerDisplayName,
  logo,
  logoWidth = 100,
  testUrl = null,
  title = "Verify With Your Bank",
  TitleIcon = IconBuildingBank,
  features = defaultFeatures,
  poweredByText = "Powered by",
  getStartedText = "Get Started",
}) => {
  const { settings, data } = useContext(DataContext);
  const [url, setUrl] = useState(testUrl);
  const [requestId, setRequestId] = useState(null);
  // Shown if the page is still here a few seconds after the redirect fired
  // (browser or embed blocked the navigation).
  const [showFallback, setShowFallback] = useState(false);

  // Use edge status check when edge is true
  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode, false, "idmax");
  useEdgeStatusCheck(isEdgeMode);

  // Send the user to the provider in the same tab. Inside an iframe, navigate the
  // top window so the bank page is not blocked by frame restrictions.
  // We usually run inside the publisher's iframe, where a cross-origin frame
  // may not navigate the top window and may only open a tab during a tap.
  // So: use the tab opened on the provider tap (see providerTab.js); when
  // we are the top window, navigate it; otherwise show the Continue button,
  // whose tap opens the provider in a new tab (the pre-redirect behaviour).
  const sendToProvider = (target) => {
    if (sendProviderTabTo(target)) return true;
    if (window.self === window.top) {
      try {
        window.location.assign(target);
        return true;
      } catch (e) {
        console.error("Provider redirect blocked:", e);
      }
    }
    return false;
  };

  const openInNewTab = (target) => {
    if (sendProviderTabTo(target)) return;
    window.open(target, "_blank");
  };

  useEffect(() => {
    if (!url) return;
    const sent = sendToProvider(url);
    // Hand-off done or not, give this page a way forward: the held tab may
    // have been closed, or the user may come back to this tab.
    const t = setTimeout(() => setShowFallback(true), sent ? FALLBACK_DELAY_MS : 0);
    return () => clearTimeout(t);
  }, [url]);

  // Polling function for edge mode
  const pollForUrl = async (requestId) => {
    const maxAttempts = 100; // 5 minutes = 300 seconds / 3 seconds per attempt
    const pollInterval = 3000; // 3 seconds
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ request_id: requestId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Poll response:", result);

          if (result.request_url) {
            setUrl(result.request_url);
            return; // Success, stop polling
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No URL received after 5 minutes");
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        }
      }
    };

    poll();
  };

  useEffect(() => {
    if (url) return;
    const fetchUrl = async () => {
      data.provider = providerName;
      // Store provider name for retrieval on return
      sessionStorage.setItem("trinsic_provider", providerName);
      // Store request_id for edge mode return flow
      if (settings?.request_id) {
        sessionStorage.setItem("edge_request_id", settings.request_id);
      }
      try {
        // Check if edge mode is enabled
        let apiUrl;
        let requestData = { ...data };

        if (data?.edge === true) {
          apiUrl = getEdgeQueueUrl();
          // Add queue parameter for idmax
          requestData.queue = "idmax";
        } else {
          apiUrl = process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax";
        }

        const response = await fetch(apiUrl, {
            method: "POST",
            body: JSON.stringify(requestData),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data_returned = await response.json();
        console.log("data", data_returned);

        if (data?.edge === true) {
          // In edge mode, we get a request_id and need to poll for the URL
          const reqId = data_returned.request_id || data.request_id;
          if (reqId) {
            setRequestId(reqId);
            console.log("Starting polling for request_id:", reqId);
            pollForUrl(reqId);
          } else {
            console.error("No request_id found in edge response");
          }
        } else {
          // In normal mode, we get the URL directly
          setUrl(data_returned.continuations.info.template.info.request_url);
        }
      } catch (error) {
        console.error("Error fetching status:", error);
      }
    };

    fetchUrl();
  }, [url, providerName]);

  // While the provider link is being generated, show the same translated
  // "Getting your verification ready…" screen the journey opens with. The
  // page navigates to the provider as soon as the link exists.
  return (
    <>
      <InitialLoadingScreen message={getLoadingMessage(settings?.language)} />
      {showFallback && url && (
        <TrinsicRedirectFallback url={url} onContinue={() => openInNewTab(url)} />
      )}
    </>
  );
};

export default TrinsicStart;
